/**
 * Badge model representing achievements in the linguistic research platform
 */
class Badge {
    constructor(id, name, description, icon, type, requirement, rarity = 'common') {
        this.id = id;
        this.name = name;
        this.description = description;
        this.icon = icon; // Emoji or icon name
        this.type = type; // 'contribution', 'exploration', 'quality', 'community', 'milestone'
        this.requirement = requirement; // Number or special condition
        this.rarity = rarity; // 'common', 'rare', 'epic', 'legendary'
        this.unlockedAt = null; // Timestamp when unlocked
    }

    unlock() {
        this.unlockedAt = Date.now();
        return this;
    }

    isUnlocked() {
        return this.unlockedAt !== null;
    }

    getRarityColor() {
        switch (this.rarity) {
            case 'common': return '#64748b'; // Gray
            case 'rare': return '#0ea5e9'; // Blue
            case 'epic': return '#8b5cf6'; // Purple
            case 'legendary': return '#f59e0b'; // Orange/Gold
            default: return '#64748b';
        }
    }

    getTypeColor() {
        switch (this.type) {
            case 'contribution': return '#10b981'; // Green
            case 'exploration': return '#3b82f6'; // Blue
            case 'quality': return '#8b5cf6'; // Purple
            case 'community': return '#f59e0b'; // Orange
            case 'milestone': return '#ef4444'; // Red
            default: return '#64748b';
        }
    }
}

// Predefined badge definitions
export const BADGE_DEFINITIONS = [
    // Contribution Badges
    new Badge('first_contribution', 'First Steps', 'Made your first endonym contribution', '🌱', 'contribution', 1, 'common'),
    new Badge('ten_contributions', 'Getting Started', 'Contributed 10 place names', '📍', 'contribution', 10, 'common'),
    new Badge('fifty_contributions', 'Dedicated Researcher', 'Contributed 50 place names', '🔬', 'contribution', 50, 'rare'),
    new Badge('hundred_contributions', 'Linguistic Scholar', 'Contributed 100 place names', '🎓', 'contribution', 100, 'epic'),
    new Badge('five_hundred_contributions', 'Master Linguist', 'Contributed 500 place names', '👑', 'contribution', 500, 'legendary'),

    // Exploration Badges
    new Badge('first_language', 'Monolingual', 'Contributed to your first language', '🗣️', 'exploration', 1, 'common'),
    new Badge('multilingual', 'Multilingual', 'Contributed to 3 different languages', '🌍', 'exploration', 3, 'rare'),
    new Badge('polyglot', 'Polyglot', 'Contributed to 5 different languages', '🗺️', 'exploration', 5, 'epic'),
    new Badge('linguistic_master', 'Linguistic Master', 'Contributed to 10 different languages', '🌐', 'exploration', 10, 'legendary'),

    // Place Type Diversity
    new Badge('urban_explorer', 'Urban Explorer', 'Contributed to cities and towns', '🏙️', 'exploration', 'urban', 'common'),
    new Badge('nature_lover', 'Nature Lover', 'Contributed to natural features', '🏞️', 'exploration', 'nature', 'common'),
    new Badge('mountain_climber', 'Mountain Climber', 'Contributed to mountain names', '⛰️', 'exploration', 'mountain', 'common'),
    new Badge('water_navigator', 'Water Navigator', 'Contributed to water body names', '🌊', 'exploration', 'water', 'common'),
    new Badge('place_type_master', 'Place Type Master', 'Contributed to all place types', '🏆', 'exploration', 'all_types', 'epic'),

    // Quality Badges
    new Badge('quality_first', 'Quality First', 'No rejected contributions in first 10', '✨', 'quality', 'perfect_10', 'rare'),
    new Badge('cultural_storyteller', 'Cultural Storyteller', 'Added cultural context to place names', '📖', 'quality', 'context', 'rare'),
    new Badge('historical_detective', 'Historical Detective', 'Found historical place names', '🕵️', 'quality', 'historical', 'epic'),

    // Community Badges
    new Badge('helpful_validator', 'Helpful Validator', 'Validated others\' contributions', '✅', 'community', 5, 'rare'),
    new Badge('knowledge_sharer', 'Knowledge Sharer', 'Shared expertise with community', '🤝', 'community', 'share', 'rare'),
    new Badge('mentor', 'Mentor', 'Helped new contributors', '🧑‍🏫', 'community', 'mentor', 'epic'),

    // Special Achievements
    new Badge('early_adopter', 'Early Adopter', 'One of the first users', '🚀', 'milestone', 'early', 'legendary'),
    new Badge('daily_contributor', 'Daily Contributor', 'Contributed for 7 consecutive days', '📅', 'milestone', 'streak_7', 'rare'),
    new Badge('weekly_warrior', 'Weekly Warrior', 'Contributed for 30 consecutive days', '🔥', 'milestone', 'streak_30', 'epic'),
    new Badge('preservation_hero', 'Preservation Hero', 'Contributed to endangered languages', '🛡️', 'milestone', 'endangered', 'legendary'),
];

export default Badge;